<!-- Revenue Dashboard with Sidebar Layout -->
<div class="revenue-dashboard-page">
  <!-- Main Content Layout with Flex -->
  <div class="main-layout">
    <!-- Sidebar Toggle Area -->
    <div class="sidebar-toggle-area">
      <p-button
        [icon]="sidebarCollapsed() ? 'pi pi-angle-right' : 'pi pi-angle-left'"
        [text]="true"
        size="small"
        (onClick)="toggleSidebar()"
        [pTooltip]="sidebarCollapsed() ? 'Expand filters' : 'Collapse filters'"
        tooltipPosition="right"
        class="sidebar-toggle-btn"
      />

      <!-- Collapsed State with Filter Button -->
      <div class="collapsed-controls" *ngIf="sidebarCollapsed()">
        <button
          class="collapsed-filter-btn"
          (click)="toggleSidebar()"
          [pTooltip]="'Filters'"
          tooltipPosition="right"
        >
          <i class="pi pi-filter"></i>
          <div class="filter-dot-indicator" *ngIf="hasActiveFilters()">
            <i class="pi pi-circle-fill"></i>
          </div>
        </button>
      </div>
    </div>

    <!-- Filter Sidebar -->
    <div class="filter-sidebar" [class.collapsed]="sidebarCollapsed()">
      <!-- Filter Content -->
      <div class="sidebar-content">
        <app-date-filter
          [formGroup]="filtersFormGroup"
          [drawerMode]="true"
          [defaultCurrency]="defaultCurrency()"
          [isLoading]="isLoading"
        />
      </div>
    </div>

    <!-- Main Content Area -->
    <div class="content-area" [class.sidebar-open]="!sidebarCollapsed()">
      <!-- Revenue Section Card -->
      <div class="revenue-section-wrapper">
        <p-card styleClass="revenue-section-card">
          <div class="section-header">
            <div class="section-header-content">
              <div class="section-header-text">
                <h2 class="section-title">
                  SUBSCRIPTION VS. NON-SUBSCRIPTION REVENUE
                </h2>
                <p class="section-subtitle">
                  See how your QPilot subscription orders contribute to your
                  overall retail sales.
                </p>
                <div
                  class="section-metadata"
                  *ngIf="lastIngestedOrder && !isLoading"
                >
                  <span class="metadata-badge">
                    <i class="pi pi-clock"></i>
                    <span class="metadata-label"
                      >Last order received #{{ lastIngestedOrder.orderId }} at
                      {{
                        lastIngestedOrder.createdDate
                          | date: "h:mma MMM dd" : getSiteTimezone()
                      }}</span
                    >
                  </span>
                </div>
                <p class="section-updated" *ngIf="lastUpdated && !isLoading">
                  <i class="pi pi-clock"></i>
                  Data last updated: {{ lastUpdated | date: "medium" }}
                </p>
              </div>
              <div class="header-actions">
                <!-- View Toggle Button -->
                <p-button
                  [icon]="
                    viewMode() === 'cards' ? 'pi pi-table' : 'pi pi-th-large'
                  "
                  [text]="true"
                  [rounded]="true"
                  size="small"
                  (onClick)="toggleViewMode()"
                  [disabled]="isLoading"
                  [pTooltip]="
                    viewMode() === 'cards'
                      ? 'Switch to comparison table'
                      : 'Switch to cards view'
                  "
                  tooltipPosition="left"
                  class="view-toggle-btn"
                />
                <!-- Download Button -->
                <button
                  pButton
                  type="button"
                  icon="pi pi-download"
                  class="p-button-text p-button-rounded download-button"
                  (click)="downloadMenu.toggle($event)"
                  [disabled]="isLoading"
                  pTooltip="Download report"
                  tooltipPosition="left"
                ></button>
                <p-menu
                  #downloadMenu
                  [model]="downloadMenuItems"
                  [popup]="true"
                  appendTo="body"
                />
              </div>
            </div>
          </div>

          <!-- Cards View -->
          <div class="revenue-cards-grid" *ngIf="viewMode() === 'cards'">
            <!-- Show skeletons while loading -->
            <ng-container *ngIf="isLoading">
              <app-revenue-card-skeleton *ngFor="let i of skeletonArray" />
            </ng-container>

            <!-- Show actual cards when loaded -->
            <ng-container *ngIf="!isLoading">
              <app-revenue-card
                *ngFor="let card of revenueData"
                [data]="card"
              />
            </ng-container>
          </div>

          <!-- Comparison Table View -->
          <div class="revenue-comparison-table" *ngIf="viewMode() === 'table'">
            <!-- Show skeleton while loading -->
            <app-revenue-comparison-table-skeleton *ngIf="isLoading" />

            <!-- Show actual table when loaded -->
            <p-table
              *ngIf="!isLoading"
              [value]="animatedTableData"
              styleClass="p-datatable-sm p-datatable-striped"
              size="small"
              breakpoint="768px"
              [scrollable]="true"
            >
              <ng-template pTemplate="header">
                <tr>
                  <th
                    style="width: 100px; min-width: 100px"
                    pFrozenColumn
                    [frozen]="true"
                    alignFrozen="left"
                  >
                    Metric
                  </th>
                  <th class="text-center">
                    <div class="column-header">
                      <span class="column-title">All Revenue</span>
                      <span class="column-badge all-revenue">ALL</span>
                    </div>
                  </th>
                  <th class="text-center">
                    <div class="column-header">
                      <span class="column-title">Subscription</span>
                      <span class="column-badge subscription">SUB</span>
                    </div>
                  </th>
                  <th class="text-center">
                    <div class="column-header">
                      <span class="column-title">Non-Subscription</span>
                      <span class="column-badge non-subscription">NON-SUB</span>
                    </div>
                  </th>
                </tr>
              </ng-template>
              <ng-template pTemplate="body" let-row>
                <tr>
                  <td
                    style="width: 100px; min-width: 100px"
                    class="metric-name"
                    pFrozenColumn
                    [frozen]="true"
                    alignFrozen="left"
                  >
                    <strong>{{ row.metric }}</strong>
                  </td>
                  <td class="metric-cell">
                    <div class="metric-value-wrapper">
                      <span class="metric-value">
                        {{
                          row.format === "currency"
                            ? (row.allRevenue
                              | currency: "USD" : "symbol" : "1.2-2")
                            : (row.allRevenue | number: "1.0-0")
                        }}
                      </span>
                      <span
                        class="metric-change"
                        [class.positive]="row.allRevenueChange > 0"
                        [class.negative]="row.allRevenueChange < 0"
                      >
                        <i
                          [class]="
                            row.allRevenueChange > 0
                              ? 'pi pi-arrow-up'
                              : 'pi pi-arrow-down'
                          "
                        ></i>
                        {{ row.allRevenueChange | number: "1.2-2" }}%
                      </span>
                    </div>
                  </td>
                  <td class="metric-cell">
                    <div class="metric-value-wrapper">
                      <span class="metric-value">
                        {{
                          row.format === "currency"
                            ? (row.subscription
                              | currency: "USD" : "symbol" : "1.2-2")
                            : (row.subscription | number: "1.0-0")
                        }}
                      </span>
                      <span
                        class="metric-change"
                        [class.positive]="row.subscriptionChange > 0"
                        [class.negative]="row.subscriptionChange < 0"
                      >
                        <i
                          [class]="
                            row.subscriptionChange > 0
                              ? 'pi pi-arrow-up'
                              : 'pi pi-arrow-down'
                          "
                        ></i>
                        {{ row.subscriptionChange | number: "1.2-2" }}%
                      </span>
                    </div>
                  </td>
                  <td class="metric-cell">
                    <div class="metric-value-wrapper">
                      <span class="metric-value">
                        {{
                          row.format === "currency"
                            ? (row.nonSubscription
                              | currency: "USD" : "symbol" : "1.2-2")
                            : (row.nonSubscription | number: "1.0-0")
                        }}
                      </span>
                      <span
                        class="metric-change"
                        [class.positive]="row.nonSubscriptionChange > 0"
                        [class.negative]="row.nonSubscriptionChange < 0"
                      >
                        <i
                          [class]="
                            row.nonSubscriptionChange > 0
                              ? 'pi pi-arrow-up'
                              : 'pi pi-arrow-down'
                          "
                        ></i>
                        {{ row.nonSubscriptionChange | number: "1.2-2" }}%
                      </span>
                    </div>
                  </td>
                </tr>
              </ng-template>
            </p-table>
          </div>
        </p-card>
        <!-- Placeholder for future sections -->
        <div class="coming-soon">
          <p class="text-muted">Additional revenue analytics coming soon...</p>
        </div>
      </div>
    </div>
  </div>
</div>

<!-- Toast Messages -->
<p-toast
  position="top-right"
  [breakpoints]="{ '920px': { width: '100%', right: '0', left: '0' } }"
/>
